import { useEffect, useMemo, useRef, useState } from 'react';
import { useDatasets } from '../../api/datasets.js';
import { useDesignTemplate, usePreviewHtml, type ChatMessage } from '../../api/copilot.js';
import {
  useDiscardTemplateDraft,
  usePublishVersion,
  useSaveTemplateDraft,
} from '../../api/report-templates.js';
import { useSaveAsFramework } from '../../api/frameworks.js';
import type {
  RenderLibrary,
  ReportTemplateDefinition,
  XlsxColumn,
  XlsxSpec,
} from '../../api/types.js';
import { FullscreenPreview } from './FullscreenPreview.js';
import { useSplitPaneSize } from '../../shell/useSplitPane.js';
import { SplitDivider } from '../../shell/SplitDivider.js';
import { consumeStarterPrompt } from '../frameworks/starter-prompt-seed.js';

/**
 * AiDesignerPanel — conversational template authoring.
 *
 * Left pane: chat log + prompt. User describes what they want; Claude
 * returns HTML + CSS. Every turn updates the live iframe preview on
 * the right.
 *
 * Right pane: iframe showing the compiled template against live data
 * from the bound dataset. Tab above it flips to a Monaco-lite HTML
 * editor for power users.
 *
 * On "Publish as new version" the current draft becomes
 * layout.kind='html' and persists through the existing template
 * versioning flow.
 */
export function AiDesignerPanel({
  templateId,
  definition,
  currentVersion,
}: {
  templateId: string;
  definition: ReportTemplateDefinition;
  currentVersion: number;
}): JSX.Element {
  const { data: datasets } = useDatasets();
  const design = useDesignTemplate();
  const preview = usePreviewHtml();
  const publish = usePublishVersion(templateId);
  const saveDraft = useSaveTemplateDraft(templateId);
  const discardDraft = useDiscardTemplateDraft(templateId);
  const saveAsFramework = useSaveAsFramework();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [draftLastSavedAt, setDraftLastSavedAt] = useState<Date | null>(null);
  const chatSplit = useSplitPaneSize('ai-designer.chat', 30, 15, 55);

  // Draft state — editable by both Claude and the human via the code pane.
  const initial = useMemo(() => initialDraft(definition), [definition]);
  const [html, setHtml] = useState<string>(initial.html);
  const [css, setCss] = useState<string>(initial.css);
  const [columnOrder] = useState<string[]>(initial.columnOrder);
  const [libraries, setLibraries] = useState<RenderLibrary[]>(initial.libraries);
  const [xlsxSpec, setXlsxSpec] = useState<XlsxSpec | null>(initial.xlsxSpec);
  const [title, setTitle] = useState<string>(
    definition.layout.kind === 'html' ? definition.layout.title : definition.layout.title,
  );
  const [datasetName] = useState<string>(definition.datasetId);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [prompt, setPrompt] = useState(
    () => consumeStarterPrompt('report-template', templateId) ?? '',
  );
  const [rightPane, setRightPane] = useState<'preview' | 'html' | 'css' | 'xlsx'>('preview');
  // Default to collapsed — most authoring sessions start with a quick
  // visual scan of the template and don't need the chat. Persisted so
  // a user who prefers chat-first only flips the toggle once.
  const CHAT_COLLAPSED_KEY = 'canopy.designer.chatCollapsed';
  const [chatCollapsed, setChatCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    try {
      const raw = localStorage.getItem(CHAT_COLLAPSED_KEY);
      if (raw === 'false') return false;
      return true;
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(CHAT_COLLAPSED_KEY, String(chatCollapsed));
    } catch {
      /* */
    }
  }, [chatCollapsed]);
  const [fsPreview, setFsPreview] = useState<boolean>(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const fsIframeRef = useRef<HTMLIFrameElement | null>(null);
  const chatEndRef = useRef<HTMLDivElement | null>(null);

  const dataset = useMemo(
    () => datasets?.find((d) => d.name === datasetName),
    [datasets, datasetName],
  );

  // If the bound dataset declares ANY required parameters, the live
  // preview can't safely call through to real data — e.g. our raw-SQL
  // adapter refuses to execute without the designated tenant-scoping
  // parameter, which is correct safety but ugly to surface in the
  // designer's ambient iframe. Fall back to mock rows so authors see
  // the template's structure; the Render tab is where live data +
  // parameter forms live.
  const datasetHasRequiredParams = useMemo(
    () => !!dataset?.parameters?.some((p) => p.required),
    [dataset],
  );

  // Initial + on-change preview.
  useEffect(() => {
    if (!html) return;
    const t = setTimeout(() => {
      const base = { html, css, title, libraries };
      const input =
        dataset && !datasetHasRequiredParams
          ? { ...base, datasetId: dataset.id, columnOrder }
          : { ...base, sampleRows: mockRowsFor(columnOrder) };
      preview.mutate(input);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    html,
    css,
    title,
    dataset?.id,
    datasetHasRequiredParams,
    columnOrder.join(','),
    libraries.join(','),
  ]);

  // Feed preview html into both the inline iframe and (if open) the
  // fullscreen modal iframe whenever the compiled HTML changes.
  useEffect(() => {
    if (!preview.data) return;
    if (iframeRef.current) iframeRef.current.srcdoc = preview.data.html;
    if (fsIframeRef.current) fsIframeRef.current.srcdoc = preview.data.html;
  }, [preview.data]);

  // Auto-scroll chat on new message.
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, design.isPending]);

  async function submitPrompt(): Promise<void> {
    if (!prompt.trim() || design.isPending) return;
    const newMessages: ChatMessage[] = [...messages, { role: 'user', content: prompt.trim() }];
    setMessages(newMessages);
    setPrompt('');
    const datasetCtx = dataset
      ? {
          name: dataset.name,
          sampleRows: mockRowsFor(columnOrder).slice(0, 5),
          columns:
            columnOrder.length > 0
              ? columnOrder.map((name) => ({ name, type: 'unknown' }))
              : (dataset.columns as Array<{ name: string; type: string }>),
        }
      : undefined;
    try {
      const result = await design.mutateAsync({
        sessionId,
        messages: newMessages,
        ...(datasetCtx ? { dataset: datasetCtx } : {}),
      });
      setSessionId(result.sessionId);
      if (result.html) setHtml(result.html);
      if (result.css !== null) setCss(result.css);
      if (result.xlsxSpec) setXlsxSpec(result.xlsxSpec as unknown as XlsxSpec);
      if (result.libraries) setLibraries(result.libraries);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: summarizeAssistant(result.assistantMessage) },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `⚠️ ${(err as Error & { body?: { message?: string } }).body?.message ?? (err as Error).message}`,
        },
      ]);
    }
  }

  // Save draft on edit (debounced). The Publish button below promotes
  // the current draft to a new immutable version on explicit action.
  const savedSnapshotRef = useRef<string>('');
  useEffect(() => {
    if (!html) return;
    const snap = JSON.stringify({
      title,
      html,
      css,
      columnOrder,
      xlsxSpec,
      libraries,
      datasetName,
    });
    if (snap === savedSnapshotRef.current) return;
    setHasUnsavedChanges(true);
    const t = setTimeout(() => {
      savedSnapshotRef.current = snap;
      const layout = {
        kind: 'html' as const,
        title,
        html,
        ...(css ? { css } : {}),
        columnOrder,
        ...(xlsxSpec ? { xlsxSpec } : {}),
        ...(libraries.length > 0 ? { libraries } : {}),
      };
      void saveDraft
        .mutateAsync({
          definition: {
            schemaVersion: '1.0.0',
            datasetId: datasetName,
            parameters: definition.parameters,
            layout,
          },
        })
        .then(() => {
          setHasUnsavedChanges(false);
          setDraftLastSavedAt(new Date());
        })
        .catch(() => {
          /* surfaced via saveDraft.error */
        });
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    html,
    css,
    title,
    columnOrder.join(','),
    libraries.join(','),
    JSON.stringify(xlsxSpec),
    datasetName,
  ]);

  function publishAsHtml(): void {
    // Publish without a body → server promotes the current draft. If no
    // draft exists (e.g. brand-new template on first edit), fall back to
    // an explicit definition built from local state so v2 still works.
    const layout = {
      kind: 'html' as const,
      title,
      html,
      ...(css ? { css } : {}),
      columnOrder,
      ...(xlsxSpec ? { xlsxSpec } : {}),
      ...(libraries.length > 0 ? { libraries } : {}),
    };
    publish.mutate(
      {
        definition: {
          schemaVersion: '1.0.0',
          datasetId: datasetName,
          parameters: definition.parameters,
          layout,
        },
      },
      {
        onSuccess: () => {
          setHasUnsavedChanges(false);
          setDraftLastSavedAt(null);
        },
      },
    );
  }

  function saveTemplateAsFramework(): void {
    const defaultName = title ? `${title} framework` : 'New framework';
    const name = window.prompt('Save this template as a reusable framework. Name:', defaultName);
    if (!name) return;
    saveAsFramework.mutate({
      name: name.trim(),
      moduleType: 'report',
      skeleton: {
        kind: 'html-handlebars',
        starterPrompt: `Start from the saved "${name}" framework and iterate.`,
        html,
        ...(xlsxSpec ? { xlsxSpec } : {}),
        bindings: [],
        outline: [],
      },
    });
  }

  function discardLocal(): void {
    if (
      !confirm('Discard unpublished changes? The template reverts to the last published version.')
    )
      return;
    discardDraft.mutate(undefined, {
      onSuccess: (fresh) => {
        // Reset local state to published layout.
        const pub = fresh.currentVersion?.definition.layout;
        if (pub && pub.kind === 'html') {
          setHtml(pub.html);
          setCss(pub.css ?? '');
          setLibraries(pub.libraries ?? []);
          setXlsxSpec(pub.xlsxSpec ?? null);
          setTitle(pub.title);
        }
        setHasUnsavedChanges(false);
        setDraftLastSavedAt(null);
        savedSnapshotRef.current = '';
      },
    });
  }

  function toggleLibrary(lib: RenderLibrary): void {
    setLibraries((prev) => (prev.includes(lib) ? prev.filter((l) => l !== lib) : [...prev, lib]));
  }

  // Collapsed mode: dedicate a narrow left rail (40px) to the
  // "Open Designer" affordance so it's clearly its own column rather
  // than a floating button that overlaps the toolbar.
  const gridCols = chatCollapsed ? '40px 0 1fr' : `${chatSplit.sizePct}% 4px 1fr`;

  return (
    <div className="card-body" style={{ padding: 0 }}>
      <div
        className={
          'ai-designer' +
          (chatCollapsed ? ' ai-designer--chat-collapsed' : '') +
          (chatSplit.isDragging ? ' split-dragging' : '')
        }
        ref={chatSplit.containerRef}
        style={{ gridTemplateColumns: gridCols }}
      >
        {chatCollapsed && (
          <button
            type="button"
            className="ai-chat-rail"
            onClick={() => setChatCollapsed(false)}
            title="Open the AI Designer chat to author with prompts"
            aria-label="Open AI Designer"
          >
            <span className="ai-chat-rail-icon" aria-hidden>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />
                <path d="M8 9h8M8 13h5" />
              </svg>
            </span>
            <span className="ai-chat-rail-label">AI&nbsp;Designer</span>
            <span className="ai-chat-rail-hint" aria-hidden>
              ›
            </span>
          </button>
        )}
        {/* LEFT: chat */}
        <div className="ai-chat">
          <div className="ai-chat-header">
            <div>
              <div className="text-sm text-dim">
                {dataset ? (
                  <>
                    Bound to <code>{dataset.name}</code>{' '}
                    <span
                      className={
                        'badge ' +
                        (dataset.mode === 'accelerated' ? 'badge--success' : 'badge--neutral')
                      }
                      style={{ marginLeft: 4 }}
                    >
                      {dataset.mode}
                    </span>
                  </>
                ) : (
                  <>No dataset resolved; preview will use sample rows.</>
                )}
              </div>
            </div>
            <div className="row" style={{ gap: 6 }}>
              {sessionId && (
                <span className="text-mute text-xs cell-mono">{sessionId.slice(0, 8)}…</span>
              )}
              <button
                type="button"
                className="btn-icon"
                onClick={() => setChatCollapsed(true)}
                title="Collapse chat"
                aria-label="Collapse chat"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10 4L6 8l4 4" />
                </svg>
              </button>
            </div>
          </div>

          <div className="ai-libraries">
            <span className="ai-libraries-label">Libraries</span>
            <div className="ai-libraries-toggles">
              {(['chartjs', 'lucide', 'fontawesome'] as const).map((lib) => (
                <button
                  key={lib}
                  type="button"
                  className={
                    'ai-lib-toggle' + (libraries.includes(lib) ? ' ai-lib-toggle--on' : '')
                  }
                  onClick={() => toggleLibrary(lib)}
                  title={LIBRARY_DESCRIPTIONS[lib]}
                >
                  <span className="ai-lib-dot" />
                  {LIBRARY_LABELS[lib]}
                </button>
              ))}
            </div>
          </div>

          <div className="ai-chat-log">
            {messages.length === 0 && (
              <div className="ai-chat-empty">
                <div className="ai-chat-empty-icon">✨</div>
                <h3>Describe the report</h3>
                <p className="text-dim text-sm">Tell Claude what you want. Example prompts:</p>
                <ul className="ai-example-prompts">
                  <li
                    onClick={() =>
                      setPrompt(
                        'Design a quarterly device-health report. Branded header, 4 KPI tiles, a per-region section that groups locations and shows subtotals, a footer with the page number.',
                      )
                    }
                  >
                    Quarterly device-health report with branded header and KPI tiles
                  </li>
                  <li
                    onClick={() =>
                      setPrompt(
                        'Simple exception report: just a table of rows where status != "online", with a red accent header and total count at the bottom.',
                      )
                    }
                  >
                    Exception report of non-online devices
                  </li>
                  <li
                    onClick={() =>
                      setPrompt(
                        'Executive one-pager: big headline metric, a two-column summary, a single compact table, restrained typography.',
                      )
                    }
                  >
                    Executive one-pager with headline metric
                  </li>
                </ul>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={'ai-msg ai-msg--' + m.role}>
                <div className="ai-msg-role">{m.role === 'user' ? 'You' : 'Claude'}</div>
                <div className="ai-msg-body">{m.content}</div>
              </div>
            ))}
            {design.isPending && (
              <div className="ai-msg ai-msg--assistant">
                <div className="ai-msg-role">Claude</div>
                <div className="ai-msg-body">
                  <span className="ai-typing" /> designing…
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <div className="ai-chat-input">
            <textarea
              className="textarea"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  void submitPrompt();
                }
              }}
              placeholder="Describe your report. Cmd/Ctrl+Enter to send."
              rows={3}
              style={{ fontFamily: 'inherit', fontSize: 'var(--font-size-md)' }}
            />
            <div className="row row--between" style={{ marginTop: 6 }}>
              <span className="text-mute text-xs">Cmd+Enter to send</span>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                disabled={!prompt.trim() || design.isPending}
                onClick={() => void submitPrompt()}
              >
                {design.isPending ? 'Designing…' : 'Send'}
              </button>
            </div>
          </div>
        </div>

        {!chatCollapsed && (
          <SplitDivider
            onDragStart={chatSplit.startDrag}
            onDoubleClick={() => chatSplit.setSize(30)}
            title="Drag to resize chat · double-click to reset"
          />
        )}

        {/* RIGHT: preview / code */}
        <div className="ai-canvas">
          <div className="ai-canvas-header">
            <div className="tabs" style={{ background: 'transparent', paddingLeft: 0, border: 0 }}>
              <button
                type="button"
                className={'tab' + (rightPane === 'preview' ? ' tab--active' : '')}
                onClick={() => setRightPane('preview')}
              >
                Preview
              </button>
              <button
                type="button"
                className={'tab' + (rightPane === 'html' ? ' tab--active' : '')}
                onClick={() => setRightPane('html')}
              >
                HTML
              </button>
              <button
                type="button"
                className={'tab' + (rightPane === 'css' ? ' tab--active' : '')}
                onClick={() => setRightPane('css')}
              >
                CSS
              </button>
              <button
                type="button"
                className={'tab' + (rightPane === 'xlsx' ? ' tab--active' : '')}
                onClick={() => setRightPane('xlsx')}
              >
                XLSX
              </button>
            </div>
            <div className="row" style={{ gap: 6 }}>
              {preview.data && (
                <span className="text-mute text-xs">
                  {preview.data.rowCount} rows · {preview.data.durationMs}ms
                </span>
              )}
              {rightPane === 'preview' && (
                <button
                  type="button"
                  className="btn-icon"
                  onClick={() => setFsPreview(true)}
                  title="Expand preview to full screen"
                  aria-label="Expand preview"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M2 6V2h4" />
                    <path d="M14 6V2h-4" />
                    <path d="M2 10v4h4" />
                    <path d="M14 10v4h-4" />
                  </svg>
                </button>
              )}
              <TemplateDraftIndicator
                saving={saveDraft.isPending}
                unsaved={hasUnsavedChanges}
                lastSavedAt={draftLastSavedAt}
              />
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={discardDraft.isPending || (!hasUnsavedChanges && !draftLastSavedAt)}
                onClick={discardLocal}
              >
                Discard
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={saveTemplateAsFramework}
                disabled={!html || saveAsFramework.isPending}
                title="Save the current layout as a reusable framework for your tenant"
              >
                {saveAsFramework.isPending ? 'Saving…' : 'Save as framework'}
              </button>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                disabled={!html || publish.isPending}
                onClick={publishAsHtml}
                title="Promote the current draft to a new immutable version"
              >
                {publish.isPending ? 'Publishing…' : `Publish v${currentVersion + 1}`}
              </button>
            </div>
          </div>

          {rightPane === 'preview' && (
            <div className="ai-preview-wrap">
              {preview.isPending && !preview.data && (
                <div className="text-dim" style={{ padding: 16 }}>
                  Compiling…
                </div>
              )}
              {preview.error &&
                (() => {
                  const body = (preview.error as Error & { body?: unknown }).body as
                    | { code?: string; message?: string; details?: { tenantParamName?: string } }
                    | undefined;
                  const isTenantBoundary = body?.code === 'tenant.boundary_violation';
                  if (isTenantBoundary) {
                    return (
                      <div className="alert alert--info" style={{ margin: 16 }}>
                        <strong>Live preview skipped</strong>
                        <p style={{ margin: '6px 0 0' }}>
                          The bound dataset requires parameter
                          <code style={{ margin: '0 4px' }}>
                            {body?.details?.tenantParamName ?? '(tenant)'}
                          </code>
                          before it can return rows. Open the <strong>Render</strong> tab to supply
                          values and render against live data. The designer preview is using mock
                          rows while you edit the layout.
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div className="alert alert--danger" style={{ margin: 16 }}>
                      <strong>Preview failed</strong>
                      <p style={{ margin: '6px 0 4px' }}>
                        {body?.message ?? String(preview.error)}
                      </p>
                      <details>
                        <summary
                          style={{
                            cursor: 'pointer',
                            fontSize: 11,
                            color: 'var(--color-text-dim)',
                          }}
                        >
                          Error details
                        </summary>
                        <pre style={{ fontSize: 11, marginTop: 4 }}>
                          {JSON.stringify(body ?? String(preview.error), null, 2)}
                        </pre>
                      </details>
                    </div>
                  );
                })()}
              <iframe ref={iframeRef} title="Preview" className="ai-preview-iframe" sandbox="" />
            </div>
          )}

          {rightPane === 'html' && (
            <div className="ai-code-wrap">
              <div className="field" style={{ padding: '12px 14px 0' }}>
                <label className="field-label">Title</label>
                <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>
              <textarea
                className="ai-code"
                value={html}
                onChange={(e) => setHtml(e.target.value)}
                spellCheck={false}
              />
              <div className="ai-code-footer">
                <span className="text-mute text-xs">
                  {html.length.toLocaleString()} chars · Handlebars syntax. Available context:
                  title, tenantName, generatedAt, rows, schema, parameters.
                </span>
              </div>
            </div>
          )}

          {rightPane === 'css' && (
            <div className="ai-code-wrap">
              <textarea
                className="ai-code"
                value={css}
                onChange={(e) => setCss(e.target.value)}
                spellCheck={false}
                placeholder="/* Your custom CSS rules */"
              />
              <div className="ai-code-footer">
                <span className="text-mute text-xs">
                  {css.length.toLocaleString()} chars · No &lt;style&gt; tags — just rules. No
                  external imports.
                </span>
              </div>
            </div>
          )}

          {rightPane === 'xlsx' && <XlsxSpecEditor spec={xlsxSpec} onChange={setXlsxSpec} />}
        </div>
      </div>

      <AiDesignerStyles />

      {fsPreview && (
        <FullscreenPreview onClose={() => setFsPreview(false)} title={title}>
          <iframe
            ref={fsIframeRef}
            title="Fullscreen preview"
            sandbox=""
            srcDoc={preview.data?.html ?? ''}
          />
        </FullscreenPreview>
      )}
    </div>
  );
}

const LIBRARY_LABELS: Record<RenderLibrary, string> = {
  chartjs: 'Chart.js',
  lucide: 'Lucide',
  fontawesome: 'Font Awesome',
};

const LIBRARY_DESCRIPTIONS: Record<RenderLibrary, string> = {
  chartjs: 'Client-side charting (bar, line, donut, scatter). Requires JS.',
  lucide: '1,000+ SVG icons. Use <i data-lucide="name">.',
  fontawesome: 'Font Awesome Free 6. Use <i class="fa-solid fa-...">.',
};

// ─── XLSX spec editor ─────────────────────────────────────────────

function XlsxSpecEditor({
  spec: maybeSpec,
  onChange,
}: {
  spec: XlsxSpec | null;
  onChange(next: XlsxSpec | null): void;
}): JSX.Element {
  if (!maybeSpec) {
    return (
      <div style={{ padding: 20 }}>
        <div className="empty-state" style={{ padding: 'var(--space-5)' }}>
          <div className="empty-state-icon">⊞</div>
          <h3>No XLSX spec yet</h3>
          <p>
            The XLSX export binds to the dataset rows by default (a flat table based on the column
            order). Ask Claude to include groups + subtotals, or start with a blank spec and build
            it here.
          </p>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() =>
              onChange({
                columns: [{ field: 'field_1', label: 'Column 1', format: 'text' }],
                freezeHeader: true,
              })
            }
          >
            Start a blank spec
          </button>
        </div>
      </div>
    );
  }

  // Narrowed local — Typescript keeps the refinement across closures.
  const spec: XlsxSpec = maybeSpec;

  // Simple structured editor. Columns table, Groups list, Grand totals list.
  function setCol(idx: number, patch: Partial<XlsxSpec['columns'][number]>): void {
    onChange({
      ...spec,
      columns: spec.columns.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    });
  }
  function addCol(): void {
    onChange({
      ...spec,
      columns: [...spec.columns, { field: 'new_field', label: 'New column', format: 'text' }],
    });
  }
  function removeCol(idx: number): void {
    onChange({ ...spec, columns: spec.columns.filter((_, i) => i !== idx) });
  }
  function addGrandTotal(): void {
    onChange({
      ...spec,
      grandTotals: [
        ...(spec.grandTotals ?? []),
        { field: spec.columns[0]?.field ?? '', agg: 'sum' },
      ],
    });
  }
  function setGrandTotal(
    idx: number,
    patch: Partial<NonNullable<XlsxSpec['grandTotals']>[number]>,
  ): void {
    onChange({
      ...spec,
      grandTotals: (spec.grandTotals ?? []).map((g, i) => (i === idx ? { ...g, ...patch } : g)),
    });
  }
  function removeGrandTotal(idx: number): void {
    onChange({ ...spec, grandTotals: (spec.grandTotals ?? []).filter((_, i) => i !== idx) });
  }
  function addGroup(): void {
    onChange({
      ...spec,
      groups: [...(spec.groups ?? []), { field: spec.columns[0]?.field ?? '', subtotals: [] }],
    });
  }
  function setGroup(idx: number, patch: Partial<NonNullable<XlsxSpec['groups']>[number]>): void {
    onChange({
      ...spec,
      groups: (spec.groups ?? []).map((g, i) => (i === idx ? { ...g, ...patch } : g)),
    });
  }
  function removeGroup(idx: number): void {
    onChange({ ...spec, groups: (spec.groups ?? []).filter((_, i) => i !== idx) });
  }

  return (
    <div className="ai-xlsx-wrap">
      <div className="ai-xlsx-row">
        <div className="field" style={{ flex: 1, margin: 0 }}>
          <label className="field-label">Sheet name</label>
          <input
            className="input input--sm"
            value={spec.sheetName ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              if (v) onChange({ ...spec, sheetName: v });
              else {
                const { sheetName: _s, ...rest } = spec;
                onChange(rest as XlsxSpec);
              }
            }}
            placeholder="Fleet"
          />
        </div>
        <div className="field" style={{ flex: 2, margin: 0 }}>
          <label className="field-label">Title (top of sheet)</label>
          <input
            className="input input--sm"
            value={spec.title ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              if (v) onChange({ ...spec, title: v });
              else {
                const { title: _t, ...rest } = spec;
                onChange(rest as XlsxSpec);
              }
            }}
            placeholder="Fleet Overview"
          />
        </div>
      </div>

      <div className="section-head">
        <div className="section-head-title">
          <h2>Columns</h2>
          <p>What appears in the sheet, in order.</p>
        </div>
        <button type="button" className="btn btn--secondary btn--sm" onClick={addCol}>
          + Column
        </button>
      </div>
      <table className="data-table">
        <thead>
          <tr>
            <th>Field</th>
            <th>Label</th>
            <th style={{ width: 120 }}>Format</th>
            <th style={{ width: 100 }}>Align</th>
            <th style={{ width: 72 }}>Width</th>
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {spec.columns.map((c, i) => (
            <tr key={i}>
              <td>
                <input
                  className="input input--sm input--mono"
                  value={c.field}
                  onChange={(e) => setCol(i, { field: e.target.value })}
                />
              </td>
              <td>
                <input
                  className="input input--sm"
                  value={c.label}
                  onChange={(e) => setCol(i, { label: e.target.value })}
                />
              </td>
              <td>
                <select
                  className="select input--sm"
                  value={c.format}
                  onChange={(e) => setCol(i, { format: e.target.value as XlsxColumn['format'] })}
                >
                  <option value="text">text</option>
                  <option value="integer">integer</option>
                  <option value="float">float</option>
                  <option value="currency">currency</option>
                  <option value="percent">percent</option>
                  <option value="date">date</option>
                  <option value="datetime">datetime</option>
                </select>
              </td>
              <td>
                <select
                  className="select input--sm"
                  value={c.align ?? ''}
                  onChange={(e) => {
                    const v = e.target.value as '' | 'left' | 'center' | 'right';
                    if (v === '') {
                      const { align: _a, ...rest } = c;
                      onChange({
                        ...spec,
                        columns: spec.columns.map((col, idx) => (idx === i ? rest : col)),
                      });
                    } else {
                      setCol(i, { align: v });
                    }
                  }}
                >
                  <option value="">auto</option>
                  <option value="left">left</option>
                  <option value="center">center</option>
                  <option value="right">right</option>
                </select>
              </td>
              <td>
                <input
                  type="number"
                  min={4}
                  max={80}
                  className="input input--sm"
                  value={c.width ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v) setCol(i, { width: parseInt(v, 10) });
                    else {
                      // Strip width from the column when cleared.
                      const { width: _w, ...rest } = c;
                      onChange({
                        ...spec,
                        columns: spec.columns.map((col, idx) => (idx === i ? rest : col)),
                      });
                    }
                  }}
                />
              </td>
              <td>
                <button type="button" className="btn-icon" onClick={() => removeCol(i)}>
                  ×
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="section-head" style={{ marginTop: 'var(--space-5)' }}>
        <div className="section-head-title">
          <h2>Groups</h2>
          <p>Each group adds an indented banner with optional subtotals.</p>
        </div>
        <button type="button" className="btn btn--secondary btn--sm" onClick={addGroup}>
          + Group
        </button>
      </div>
      {(spec.groups ?? []).length === 0 ? (
        <div className="text-mute text-sm" style={{ padding: '8px 0' }}>
          No groups — rows will render flat.
        </div>
      ) : (
        (spec.groups ?? []).map((g, i) => (
          <div key={i} className="card" style={{ marginBottom: 'var(--space-2)' }}>
            <div className="card-body" style={{ padding: 'var(--space-3)' }}>
              <div className="row" style={{ gap: 'var(--space-2)' }}>
                <div className="field" style={{ flex: 1, margin: 0 }}>
                  <label className="field-label">Group by field</label>
                  <select
                    className="select input--sm"
                    value={g.field}
                    onChange={(e) => setGroup(i, { field: e.target.value })}
                  >
                    {spec.columns.map((c) => (
                      <option key={c.field} value={c.field}>
                        {c.field}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field" style={{ flex: 1, margin: 0 }}>
                  <label className="field-label">Label</label>
                  <input
                    className="input input--sm"
                    value={g.label ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v) setGroup(i, { label: v });
                      else {
                        const { label: _l, ...rest } = g;
                        onChange({
                          ...spec,
                          groups: (spec.groups ?? []).map((gg, idx) => (idx === i ? rest : gg)),
                        });
                      }
                    }}
                  />
                </div>
                <button
                  type="button"
                  className="btn-icon"
                  onClick={() => removeGroup(i)}
                  aria-label="Remove group"
                >
                  ×
                </button>
              </div>
              <div className="text-mute text-xs" style={{ marginTop: 6 }}>
                Subtotals:{' '}
                {(g.subtotals ?? []).length === 0 ? (
                  <span>none</span>
                ) : (
                  (g.subtotals ?? []).map((s) => (
                    <code key={s.field + s.agg} style={{ marginRight: 4 }}>
                      {s.agg}({s.field})
                    </code>
                  ))
                )}{' '}
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() =>
                    setGroup(i, {
                      subtotals: [
                        ...(g.subtotals ?? []),
                        {
                          field:
                            spec.columns.find(
                              (c) =>
                                c.format === 'integer' ||
                                c.format === 'float' ||
                                c.format === 'currency',
                            )?.field ??
                            spec.columns[0]?.field ??
                            '',
                          agg: 'sum',
                        },
                      ],
                    })
                  }
                >
                  + subtotal
                </button>
                {(g.subtotals ?? []).length > 0 && (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => setGroup(i, { subtotals: [] })}
                  >
                    clear
                  </button>
                )}
              </div>
            </div>
          </div>
        ))
      )}

      <div className="section-head" style={{ marginTop: 'var(--space-5)' }}>
        <div className="section-head-title">
          <h2>Grand totals</h2>
          <p>One row at the bottom of the sheet.</p>
        </div>
        <button type="button" className="btn btn--secondary btn--sm" onClick={addGrandTotal}>
          + Total
        </button>
      </div>
      {(spec.grandTotals ?? []).length === 0 ? (
        <div className="text-mute text-sm" style={{ padding: '8px 0' }}>
          No grand totals.
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Field</th>
              <th style={{ width: 160 }}>Aggregation</th>
              <th style={{ width: 200 }}>Label</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {(spec.grandTotals ?? []).map((gt, i) => (
              <tr key={i}>
                <td>
                  <select
                    className="select input--sm"
                    value={gt.field}
                    onChange={(e) => setGrandTotal(i, { field: e.target.value })}
                  >
                    {spec.columns.map((c) => (
                      <option key={c.field} value={c.field}>
                        {c.field}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    className="select input--sm"
                    value={gt.agg}
                    onChange={(e) =>
                      setGrandTotal(i, {
                        agg: e.target.value as XlsxSpec['grandTotals'] extends undefined
                          ? never
                          : 'sum',
                      })
                    }
                  >
                    <option value="sum">sum</option>
                    <option value="avg">avg</option>
                    <option value="min">min</option>
                    <option value="max">max</option>
                    <option value="count">count</option>
                    <option value="distinct_count">distinct_count</option>
                  </select>
                </td>
                <td>
                  <input
                    className="input input--sm"
                    value={gt.label ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v) setGrandTotal(i, { label: v });
                      else {
                        const { label: _l, ...rest } = gt;
                        onChange({
                          ...spec,
                          grandTotals: (spec.grandTotals ?? []).map((g, idx) =>
                            idx === i ? rest : g,
                          ),
                        });
                      }
                    }}
                  />
                </td>
                <td>
                  <button type="button" className="btn-icon" onClick={() => removeGrandTotal(i)}>
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ marginTop: 'var(--space-5)' }}>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => onChange(null)}>
          Remove XLSX spec
        </button>
      </div>
    </div>
  );
}

// ─── Starting-point helpers ───────────────────────────────────────

function TemplateDraftIndicator({
  saving,
  unsaved,
  lastSavedAt,
}: {
  saving: boolean;
  unsaved: boolean;
  lastSavedAt: Date | null;
}): JSX.Element {
  if (saving) {
    return <span className="draft-indicator draft-indicator--saving">◌ Saving draft…</span>;
  }
  if (unsaved) {
    return <span className="draft-indicator draft-indicator--unsaved">● Unsaved changes</span>;
  }
  if (lastSavedAt) {
    return (
      <span className="draft-indicator draft-indicator--saved" title={lastSavedAt.toLocaleString()}>
        ✓ Draft saved
      </span>
    );
  }
  return <></>;
}

function initialDraft(definition: ReportTemplateDefinition): {
  html: string;
  css: string;
  columnOrder: string[];
  libraries: RenderLibrary[];
  xlsxSpec: XlsxSpec | null;
} {
  if (definition.layout.kind === 'html') {
    return {
      html: definition.layout.html,
      css: definition.layout.css ?? '',
      columnOrder: definition.layout.columnOrder ?? [],
      libraries: definition.layout.libraries ?? [],
      xlsxSpec: definition.layout.xlsxSpec ?? null,
    };
  }
  // Migrating a tabular layout — seed HTML that reproduces it, so a
  // user converting their tabular template to HTML has a decent start.
  const cols = definition.layout.columns;
  const columnOrder = cols.map((c) => c.field);
  const headers = cols
    .map((c) => `<th${c.align === 'right' ? ' class="num"' : ''}>${escape(c.label)}</th>`)
    .join('');
  const cells = cols
    .map((c) => `<td${c.align === 'right' ? ' class="num"' : ''}>{{format this.${c.field}}}</td>`)
    .join('');
  const html = `<div class="doc">
  <header class="doc-header">
    <h1>{{title}}</h1>
    <div class="doc-sub">{{tenantName}} · {{formatDate generatedAt "YYYY-MM-DD HH:mm"}}</div>
  </header>
  <table>
    <thead><tr>${headers}</tr></thead>
    <tbody>{{#each rows}}<tr>${cells}</tr>{{/each}}</tbody>
  </table>
</div>`;
  const css = `body { font-family: -apple-system, Inter, system-ui, Arial, sans-serif; color: #1a1a1a; padding: 32px 40px; font-size: 12px; }
.doc-header { border-bottom: 2px solid #1a1a1a; padding-bottom: 10px; margin-bottom: 16px; }
.doc-header h1 { font-size: 20px; font-weight: 700; margin: 0; }
.doc-sub { font-size: 10px; color: #6a6a70; margin-top: 4px; }
table { width: 100%; border-collapse: collapse; }
th { background: #f4f4f7; text-align: left; padding: 6px 10px; font-size: 10px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: #3a3a3a; border-bottom: 1.5px solid #1a1a1a; }
td { padding: 6px 10px; border-bottom: 1px solid #e4e4e8; }
.num { text-align: right; font-variant-numeric: tabular-nums; }`;

  // Seed an XLSX spec from the column list so the migration isn't lossy.
  const xlsxSpec: XlsxSpec = {
    columns: cols.map((c) => ({
      field: c.field,
      label: c.label,
      format: 'text' as const,
      align: c.align,
    })),
    freezeHeader: true,
  };
  return { html, css, columnOrder, libraries: [], xlsxSpec };
}

function escape(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function mockRowsFor(columnOrder: string[]): Array<Record<string, unknown>> {
  // Light placeholder data when no dataset is resolvable. Used only for
  // the preview path; real renders always pull from the dataset.
  if (columnOrder.length === 0) {
    return [{ sample: 'value 1' }, { sample: 'value 2' }];
  }
  return [1, 2, 3].map((i) => Object.fromEntries(columnOrder.map((c) => [c, inferSample(c, i)])));
}

function inferSample(field: string, i: number): unknown {
  if (/count|total|num|amount|n$|qty|devices/i.test(field)) return i * 3 + 1;
  if (/region/i.test(field)) return ['us-east', 'us-west', 'us-central'][i - 1] ?? 'us-east';
  if (/status/i.test(field)) return ['online', 'offline', 'degraded'][i - 1] ?? 'online';
  if (/date|at$/i.test(field)) return new Date().toISOString();
  return `Sample ${i}`;
}

function summarizeAssistant(raw: string): string {
  // The API returns the full HTML/CSS blob; showing it in chat is
  // noisy. We already pipe HTML+CSS into the editor and preview. Here
  // we just acknowledge the turn.
  const cssBoundary = '<!-- __CSS_BOUNDARY__ -->';
  const htmlOnly = raw.split(cssBoundary)[0] ?? raw;
  const firstElementLine =
    htmlOnly
      .trim()
      .split('\n')
      .find((l) => l.trim().length > 0) ?? '';
  const approxChars = raw.length.toLocaleString();
  return `Updated the template (${approxChars} chars). Top of HTML: ${firstElementLine.slice(0, 120)}…`;
}

// ─── Styles ───────────────────────────────────────────────────────

function AiDesignerStyles(): JSX.Element {
  return (
    <style>{`
      .ai-designer {
        position: relative;
        display: grid;
        /* Inline style from useSplitPaneSize drives the actual columns.
           When user drags, we skip transition to avoid fighting the drag;
           collapse still animates via CSS since the inline value changes
           atomically.  */
        grid-template-columns: 30% 4px 1fr;
        min-height: 640px;
        height: calc(100vh - 260px);
      }
      .ai-designer.split-dragging {
        /* no transition while dragging */
      }
      .ai-designer--chat-collapsed .ai-chat {
        overflow: hidden;
        opacity: 0;
        pointer-events: none;
      }
      .ai-designer--chat-collapsed .ai-chat {
        overflow: hidden;
        opacity: 0;
        pointer-events: none;
      }
      /* Vertical "AI Designer" rail shown when the chat is collapsed.
       * Occupies its own grid column (40px) so it never overlaps the
       * editor toolbar. Click anywhere on the rail to expand. */
      .ai-chat-rail {
        grid-column: 1;
        grid-row: 1;
        align-self: stretch;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        gap: var(--space-3);
        padding: var(--space-4) 0;
        background: var(--color-surface-2);
        border-right: 1px solid var(--color-border-subtle);
        color: var(--color-text-secondary);
        cursor: pointer;
        transition: background var(--duration-fast) var(--ease-out),
                    color var(--duration-fast) var(--ease-out);
        border-top: 0;
        border-bottom: 0;
        border-left: 0;
      }
      .ai-chat-rail:hover {
        background: var(--color-surface-3);
        color: var(--color-text-primary);
      }
      .ai-chat-rail-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border-radius: var(--radius-md);
        background: var(--color-surface-1);
        border: 1px solid var(--color-border-subtle);
      }
      .ai-chat-rail:hover .ai-chat-rail-icon {
        border-color: var(--color-accent-border);
      }
      /* Vertical "AI Designer" text. writing-mode rotates the glyphs
       * upright; transform flips so it reads bottom-to-top. */
      .ai-chat-rail-label {
        writing-mode: vertical-rl;
        transform: rotate(180deg);
        font-size: var(--font-size-sm);
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        margin-top: var(--space-2);
      }
      .ai-chat-rail-hint {
        margin-top: auto;
        font-size: var(--font-size-md);
        color: var(--color-text-tertiary);
      }
      .ai-chat {
        display: flex;
        flex-direction: column;
        border-right: 1px solid var(--color-border-subtle);
        background: var(--color-surface-1);
        min-height: 0;
      }
      .ai-chat-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-3) var(--space-4);
        border-bottom: 1px solid var(--color-border-subtle);
        min-height: 44px;
      }
      .ai-chat-log {
        flex: 1;
        overflow-y: auto;
        padding: var(--space-4);
      }
      .ai-chat-empty {
        text-align: center;
        padding: var(--space-6) var(--space-3);
        color: var(--color-text-secondary);
      }
      .ai-chat-empty-icon {
        font-size: 22px;
        width: 40px; height: 40px; border-radius: var(--radius-md);
        background: var(--color-accent-softer);
        color: var(--color-accent);
        display: inline-flex; align-items: center; justify-content: center;
        margin-bottom: var(--space-3);
      }
      .ai-chat-empty h3 { font-size: var(--font-size-md); font-weight: var(--font-weight-semi); margin: 0 0 8px; color: var(--color-text-primary); }
      .ai-example-prompts {
        list-style: none;
        padding: 0;
        margin: var(--space-3) 0 0;
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .ai-example-prompts li {
        padding: 8px 12px;
        border: 1px dashed var(--color-border);
        border-radius: var(--radius-md);
        font-size: var(--font-size-sm);
        color: var(--color-text-secondary);
        cursor: pointer;
        transition: background var(--duration-fast) var(--ease-out), border-color var(--duration-fast) var(--ease-out), color var(--duration-fast) var(--ease-out);
        text-align: left;
      }
      .ai-example-prompts li:hover {
        background: var(--color-accent-softer);
        border-color: var(--color-accent-border);
        border-style: solid;
        color: var(--color-text-primary);
      }
      .ai-msg {
        display: flex;
        flex-direction: column;
        gap: 4px;
        margin-bottom: var(--space-4);
      }
      .ai-msg-role {
        font-size: var(--font-size-xxs);
        font-weight: var(--font-weight-semi);
        letter-spacing: var(--letter-wide);
        text-transform: uppercase;
        color: var(--color-text-tertiary);
      }
      .ai-msg-body {
        font-size: var(--font-size-md);
        line-height: var(--line-height-normal);
        color: var(--color-text-primary);
        white-space: pre-wrap;
      }
      .ai-msg--user .ai-msg-body {
        background: var(--color-accent-softer);
        border: 1px solid var(--color-accent-border);
        border-radius: var(--radius-md);
        padding: 8px 12px;
      }
      .ai-msg--assistant .ai-msg-body {
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-md);
        padding: 8px 12px;
      }
      .ai-typing {
        display: inline-block;
        width: 6px; height: 6px;
        background: var(--color-accent);
        border-radius: 50%;
        margin-right: 6px;
        animation: ai-blink 1s infinite;
      }
      @keyframes ai-blink { 0%, 60% { opacity: 1; } 30% { opacity: 0.3; } 100% { opacity: 1; } }
      .ai-chat-input {
        padding: var(--space-3) var(--space-4);
        border-top: 1px solid var(--color-border-subtle);
      }
      .ai-libraries {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: 8px var(--space-4);
        border-bottom: 1px solid var(--color-border-subtle);
        background: var(--color-surface-2);
      }
      .ai-libraries-label {
        font-size: var(--font-size-xxs);
        font-weight: var(--font-weight-semi);
        letter-spacing: var(--letter-wide);
        text-transform: uppercase;
        color: var(--color-text-tertiary);
      }
      .ai-libraries-toggles { display: flex; gap: 6px; flex-wrap: wrap; }
      .ai-lib-toggle {
        display: inline-flex; align-items: center; gap: 5px;
        padding: 3px 10px;
        border-radius: var(--radius-full);
        background: transparent;
        border: 1px solid var(--color-border);
        color: var(--color-text-secondary);
        font-size: var(--font-size-xs);
        font-weight: var(--font-weight-medium);
        cursor: pointer;
        transition: all var(--duration-fast) var(--ease-out);
      }
      .ai-lib-toggle:hover { color: var(--color-text-primary); border-color: var(--color-border-strong); }
      .ai-lib-toggle--on {
        background: var(--color-accent-soft);
        border-color: var(--color-accent-border);
        color: var(--color-accent);
      }
      .ai-lib-dot {
        display: inline-block; width: 6px; height: 6px; border-radius: 50%;
        background: currentColor; opacity: 0.55;
      }
      .ai-lib-toggle--on .ai-lib-dot { opacity: 1; }

      .ai-xlsx-wrap {
        padding: 16px 20px;
        overflow-y: auto;
        flex: 1;
      }
      .ai-xlsx-row {
        display: flex;
        gap: var(--space-3);
        margin-bottom: var(--space-4);
      }

      .ai-canvas {
        display: flex;
        flex-direction: column;
        background: var(--color-surface-1);
        min-height: 0;
      }
      .ai-canvas-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 6px var(--space-4);
        border-bottom: 1px solid var(--color-border-subtle);
        min-height: 44px;
      }
      .ai-preview-wrap {
        flex: 1;
        min-height: 0;
        background: #f0f1f6;
        padding: 18px;
        display: flex;
        align-items: flex-start;
        justify-content: center;
        overflow: auto;
      }
      .ai-preview-iframe {
        background: #fff;
        border: 0;
        box-shadow: 0 4px 22px rgba(0,0,0,0.28), 0 1px 2px rgba(0,0,0,0.18);
        width: min(720px, 100%);
        height: 920px;
        border-radius: 2px;
      }
      .ai-code-wrap {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
        background: var(--color-surface-1);
      }
      .ai-code {
        flex: 1;
        min-height: 0;
        width: 100%;
        background: var(--color-bg);
        color: var(--color-text-primary);
        font-family: var(--font-mono);
        font-size: var(--font-size-sm);
        line-height: 1.6;
        padding: 14px 16px;
        border: 0;
        border-top: 1px solid var(--color-border-subtle);
        outline: none;
        resize: none;
      }
      .ai-code:focus { box-shadow: inset 0 0 0 2px var(--color-border-focus); }
      .ai-code-footer {
        padding: 8px var(--space-4);
        border-top: 1px solid var(--color-border-subtle);
        background: var(--color-surface-2);
      }
    `}</style>
  );
}
